<!--
  告警配置 · 告警接收人：飞书群接收人列表 + 新增 / 编辑 / 启停 / 删除
  数据来源：告警接收人列表 / 新增编辑 / 启停 / 删除 四个接口
-->
<template>
  <div class="recipient-pane">
    <div class="card">
      <div class="card-header">
        <h3 style="margin-top: 0;">
          <span>飞书群组接收人</span>
          <span class="ch-pt">Destinatários de Grupos Feishu</span>
        </h3>
        <div class="hd-actions">
          <el-button
            size="small" type="primary" icon="el-icon-plus"
            @click="openAdd"
          >
            <bi-label zh="新增接收人" pt="Novo Destinatário" inline />
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="loading" :data="rows" size="mini"
        border
        style="width: 100%"
      >
        <el-table-column prop="name" min-width="180">
          <template slot="header">
            <bi-label zh="接收群名称" pt="Nome do Grupo" />
          </template>
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.name }}</div>
          </template>
        </el-table-column>

        <!--        <el-table-column prop="chatId" min-width="170">-->
        <!--          <template slot="header">-->
        <!--            <bi-label zh="飞书群 ChatID" pt="ChatID do Grupo Feishu" />-->
        <!--          </template>-->
        <!--          <template slot-scope="{ row }">-->
        <!--            <div class="cell-num ellipsis" :title="row.chatId">{{ row.chatId || '—' }}</div>-->
        <!--          </template>-->
        <!--        </el-table-column>-->

        <el-table-column min-width="220">
          <template slot="header">
            <bi-label zh="@机器人 Webhook" pt="@ Webhook do Bot" />
          </template>
          <template slot-scope="{ row }">
            <div class="cell-webhook ellipsis" :title="row.webhookMasked">
              {{ row.webhookMasked || '—' }}
            </div>
            <span :class="['cfg-tag', row.webhookConfigured ? 'cfg-ok' : 'cfg-no']">
              {{ row.webhookConfigured ? '已配置' : '未配置' }}
              <span class="tag-pt">/ {{ row.webhookConfigured ? 'Configurado' : 'Não configurado' }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column min-width="200">
          <template slot="header">
            <bi-label zh="告警类型订阅" pt="Assinatura de Tipos de Alerta" />
          </template>
          <template slot-scope="{ row }">
            <div class="subs-cell">
              <span
                v-for="s in parseSubs(row.subscribeTypes)" :key="s"
                class="tag" :class="typeCls(s)"
              >
                {{ typeZh(s) }}<span class="tag-pt"> / {{ typePt(s) }}</span>
              </span>
              <span v-if="!parseSubs(row.subscribeTypes).length" class="cell-empty">
                全部类型<span class="tag-pt"> / Todos os tipos</span>
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center">
          <template slot="header">
            <bi-label zh="连通性" pt="Saúde" />
          </template>
          <template slot-scope="{ row }">
            <span :class="['st-tag', row.healthStatus === 1 ? 'st-on' : 'st-err']">
              {{ row.healthStatus === 1 ? '正常' : '异常' }}
              <span class="st-pt">/ {{ row.healthStatus === 1 ? 'Normal' : 'Falha' }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column min-width="130">
          <template slot="header">
            <bi-label zh="最近测试 / 发送" pt="Últ. Teste / Envio" />
          </template>
          <template slot-scope="{ row }">
            <div class="cell-time">{{ fmtTime(row.lastTestTime) }}</div>
            <div class="cell-time cell-sub">{{ fmtTime(row.lastSendTime) }}</div>
          </template>
        </el-table-column>

        <el-table-column width="80" align="center" fixed="right">
          <template slot="header">
            <bi-label zh="启用" pt="Ativação" />
          </template>
          <template slot-scope="{ row }">
            <toggle-switch
              :value="row.enable === 1"
              on-text="启用 / Ativado"
              off-text="停用 / Desativado"
              @click="handleToggle(row)"
            />
          </template>
        </el-table-column>

        <el-table-column width="200" align="center" fixed="right">
          <template slot="header">
            <bi-label zh="操作" pt="Ações" />
          </template>
          <template slot-scope="{ row }">
            <div class="act-group">
              <a
                class="act-edit" :class="{ 'is-disabled': isEnabled(row) }"
                :title="isEnabled(row) ? '启用中不可编辑，请先停用 / Desative antes de editar' : ''"
                @click="handleEdit(row)"
              >编辑<span class="act-pt"> / Editar</span></a>
              <a
                class="act-del" :class="{ 'is-disabled': isEnabled(row) }"
                :title="isEnabled(row) ? '启用中不可删除，请先停用 / Desative antes de excluir' : ''"
                @click="confirmDelete(row)"
              >删除<span class="act-pt"> / Excluir</span></a>
            </div>
          </template>
        </el-table-column>

        <div slot="empty" class="tbl-empty">
          暂无接收人，请点击「+ 新增接收人」添加
          <span class="empty-pt">/ Nenhum destinatário; clique em "+ Novo Destinatário"</span>
        </div>
      </el-table>
    </div>

    <!-- 新增 / 编辑接收人 -->
    <recipient-dialog
      :visible.sync="dialogVisible"
      :mode="dialogMode"
      :row="currentRow"
      @save="handleSave"
    />

    <!-- 启停 / 删除二次确认 -->
    <confirm-dialog
      :visible.sync="confirmVisible"
      :title="confirmOpts.title"
      :title-pt="confirmOpts.titlePt"
      :subtitle="confirmOpts.subtitle"
      :desc="confirmOpts.desc"
      :desc-pt="confirmOpts.descPt"
      :ok-text="confirmOpts.okText"
      :ok-text-pt="confirmOpts.okTextPt"
      :kind="confirmOpts.kind"
      @confirm="confirmOpts.onOk"
    />
  </div>
</template>

<script>
import BiLabel from './BiLabel';
import ToggleSwitch from './ToggleSwitch';
import ConfirmDialog from './ConfirmDialog';
import RecipientDialog from './RecipientDialog';
import { ALERT_TYPE_LABEL } from '../mock';
import {
  getInvoiceSubjectStatusMonitorRecipientList,
  getInvoiceSubjectStatusMonitorRecipientSave,
  getInvoiceSubjectStatusMonitorRecipientUpdateStatus,
  getInvoiceSubjectStatusMonitorRecipientDelete
} from '@/api/invoiceCenter';

export default {
  name: 'RecipientPane',
  components: { BiLabel, ToggleSwitch, ConfirmDialog, RecipientDialog },
  data() {
    return {
      // 接收人列表：字段同列表接口（id / name / chatId / webhookMasked / subscribeTypes / enable 等）
      rows: [],
      loading: false,
      dialogVisible: false,
      dialogMode: 'add',
      currentRow: null,
      confirmVisible: false,
      confirmOpts: {
        title: '', titlePt: '', subtitle: '', desc: '', descPt: '',
        okText: '确认', okTextPt: 'Confirmar', kind: 'ok', onOk: () => {}
      }
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    // ============ 接口 ============
    /** 拉取接收人列表：失败只提示，列表保持空态 */
    async fetchList() {
      this.loading = true;
      try {
        const res = await getInvoiceSubjectStatusMonitorRecipientList();
        this.rows = Array.isArray(res && res.data) ? res.data : [];
      } catch (e) {
        this.rows = [];
        this.$message.error('接收人列表接口请求失败，请稍后重试 / Falha ao consultar destinatários');
        console.error('[告警接收人] 列表接口请求失败', e);
      } finally {
        this.loading = false;
      }
    },
    /** 新增 / 编辑保存：成功刷新列表，失败只提示 */
    async handleSave({ mode, form }) {
      const payload = {
        name: form.name,
        chatId: form.chatId,
        webhookUrl: form.webhookUrl,
        // 订阅类型：数字数组转 JSON 字符串；未勾选（空）表示订阅全部类型
        subscribeTypes: form.subs && form.subs.length ? JSON.stringify(form.subs) : '',
        enable: form.enable ? 1 : 0
      };
      if (mode === 'edit') payload.id = this.currentRow && this.currentRow.id;
      const res = await this.callApi('save', () => getInvoiceSubjectStatusMonitorRecipientSave(payload));
      if (!res) {
        this.$message.error(`${form.name} 保存失败，请稍后重试 / Falha ao salvar ${form.name}`);
        return;
      }
      await this.fetchList();
      this.$message.success(mode === 'add'
        ? `已新增接收人「${form.name}」/ Destinatário "${form.name}" adicionado`
        : `已更新接收人「${form.name}」/ Destinatário "${form.name}" atualizado`);
    },
    /** 启用 / 停用接收人：成功后刷新列表 */
    async toggleRecipient(row, enable) {
      const res = await this.callApi('toggle', () => getInvoiceSubjectStatusMonitorRecipientUpdateStatus({
        id: row.id,
        enable
      }));
      if (!res) {
        this.$message.error(`${row.name} 启停失败，请稍后重试 / Falha ao alterar ${row.name}`);
        return false;
      }
      await this.fetchList();
      return true;
    },
    /** 删除接收人：成功后刷新列表 */
    async deleteRecipient(row) {
      const res = await this.callApi('remove', () => getInvoiceSubjectStatusMonitorRecipientDelete({ id: row.id }));
      if (!res) {
        this.$message.error(`${row.name} 删除失败，请稍后重试 / Falha ao excluir ${row.name}`);
        return false;
      }
      await this.fetchList();
      return true;
    },
    /**
     * 统一封装接口调用：失败只打日志并返回 null，由调用方提示
     * @param {string} label 接口名称（仅用于日志）
     * @param {Function} fn 真正的请求函数
     * @returns {Promise<object|null>} 成功返回 res，失败返回 null
     */
    async callApi(label, fn) {
      try {
        return await fn();
      } catch (e) {
        console.error(`[告警接收人] ${label} 请求失败`, e);
        return null;
      }
    },

    // ============ 交互 ============
    /** 打开新增弹窗 */
    openAdd() {
      this.dialogMode = 'add';
      this.currentRow = null;
      this.dialogVisible = true;
    },
    /** 是否启用中：启用中的接收人不允许编辑 / 删除，需先停用 */
    isEnabled(row) {
      return !!(row && row.enable === 1);
    },
    /** 打开编辑弹窗，传入当前行回填；启用中拦截 */
    handleEdit(row) {
      if (this.isEnabled(row)) {
        this.$message.warning('启用中不可编辑，请先停用该接收人 / Desative o destinatário antes de editar');
        return;
      }
      this.dialogMode = 'edit';
      this.currentRow = row;
      this.dialogVisible = true;
    },
    /** 点击启用开关：先弹二次确认，确认后才调启停接口 */
    handleToggle(row) {
      const willEnable = row.enable !== 1;
      const subsText = this.parseSubs(row.subscribeTypes).map(s => this.typeZh(s)).join('、') || '全部类型';
      this.confirmOpts = {
        title: willEnable ? '启用接收人' : '停用接收人',
        titlePt: willEnable ? 'Ativar Destinatário' : 'Desativar Destinatário',
        subtitle: row.name,
        desc: willEnable
          ? `启用后该飞书群将接收 <b>${subsText}</b> 的告警推送。`
          : '停用后该飞书群 <b>不再接收任何告警推送</b>，配置将保留。',
        descPt: willEnable
          ? `Após a ativação, este grupo Feishu receberá pushes do(s) tipo(s) <b>${subsText}</b>.`
          : 'Após a desativação, este grupo Feishu <b>não receberá mais pushes de alerta</b>; a configuração será mantida.',
        okText: willEnable ? '确认启用' : '确认停用',
        okTextPt: willEnable ? 'Confirmar Ativação' : 'Confirmar Desativação',
        kind: willEnable ? 'ok' : 'warn',
        onOk: async() => {
          const ok = await this.toggleRecipient(row, willEnable ? 1 : 0);
          if (ok) {
            this.$message.success(`${row.name} 已${willEnable ? '启用' : '停用'} / ${row.name} ${willEnable ? 'ativado' : 'desativado'}`);
          }
        }
      };
      this.confirmVisible = true;
    },
    /** 删除接收人：启用中拦截；停用状态下二次确认后调删除接口 */
    confirmDelete(row) {
      if (this.isEnabled(row)) {
        this.$message.warning('启用中不可删除，请先停用该接收人 / Desative o destinatário antes de excluir');
        return;
      }
      this.confirmOpts = {
        title: '删除接收人',
        titlePt: 'Excluir Destinatário',
        subtitle: row.name,
        desc: '确认后该飞书群将<b>不再接收任何告警推送</b>，配置信息将被移除。',
        descPt: 'Após a confirmação, este grupo Feishu <b>não receberá mais pushes de alerta</b> e a configuração será removida.',
        okText: '确认删除',
        okTextPt: 'Confirmar Exclusão',
        kind: 'danger',
        onOk: async() => {
          const ok = await this.deleteRecipient(row);
          if (ok) this.$message.success(`已删除 ${row.name} / ${row.name} excluído`);
        }
      };
      this.confirmVisible = true;
    },

    // ============ 展示工具 ============
    /**
     * 订阅类型解析：接口返回字符串（如 "[1,2,3]" 或 "1,2,3"）→ 数字数组
     * 空值按接口约定视为「订阅全部类型」，这里返回空数组由模板展示为「全部类型」
     */
    parseSubs(v) {
      if (!v) return [];
      if (Array.isArray(v)) return v.map(Number);
      const s = String(v).trim();
      if (!s) return [];
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map(Number);
      } catch (e) {
        // 非 JSON：按逗号分隔再转数字
      }
      return s.split(',').map(x => Number(String(x).trim())).filter(n => !isNaN(n));
    },
    /** 告警类型中文（1紧急告警 2恢复通知 3警告通知） */
    typeZh(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].zh) || v;
    },
    /** 告警类型葡语 */
    typePt(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].pt) || v;
    },
    /** 告警类型标签配色：紧急红 / 恢复绿 / 警告橙 */
    typeCls(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].cls) || 'tag-gray';
    },
    /** ISO 时间 → MM-DD HH:mm:ss */
    fmtTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const p = n => String(n).padStart(2, '0');
      return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    }
  }
};
</script>

<style scoped lang="scss">
.card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 20px;
}
.card-header {
  border-bottom: 1px solid #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.card-header h3 {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.card-header h3 .ch-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
  margin-top: 2px;
}
.hd-actions {
  display: flex;
  gap: 8px;
}

.cell-main {
  font-weight: 600;
  font-size: 12px;
  color: #1d2129;
}
.cell-num {
  font-size: 12px;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.cell-webhook {
  font-size: 12px;
  color: #86909c;
  display: inline-block;
  max-width: 170px;
  vertical-align: middle;
}
.cell-time {
  font-size: 12px;
  color: #4e5969;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.cell-time.cell-sub {
  color: #86909c;
}
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cell-empty {
  color: #86909c;
  font-size: 12px;
}

.cfg-tag {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
}
.cfg-tag .tag-pt {
  font-size: 12px;
  font-weight: 400;
}
.cfg-ok { background: #e8f7e9; color: #00b42a; }
.cfg-no { background: #f2f3f5; color: #86909c; }

.subs-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag .tag-pt {
  font-size: 12px;
  font-weight: 400;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-info { background: #e6f0ff; color: #165dff; }
.tag-gray { background: #f2f3f5; color: #4e5969; }

.st-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
}
.st-tag .st-pt {
  font-size: 12px;
  font-weight: 400;
}
.st-on { background: #e8f7e9; color: #00b42a; }
.st-err { background: #ffecec; color: #f53f3f; }

.act-group {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.act-group a {
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.act-group a .act-pt {
  font-size: 12px;
  color: #86909c;
}
.act-edit { color: #00a551; }
.act-del { color: #f53f3f; }
/* 启用中：编辑 / 删除置灰不可点（普通 a 标签的 disabled 属性不生效，需用类名 + 方法内拦截） */
.act-group a.is-disabled {
  color: #c9cdd4;
  cursor: not-allowed;
}
.act-group a.is-disabled .act-pt { color: #c9cdd4; }

.tbl-empty {
  padding: 24px 0;
  font-size: 12px;
  color: #86909c;
}
.tbl-empty .empty-pt {
  display: block;
  font-size: 12px;
  margin-top: 2px;
}
</style>
